import { logError } from '@/lib/logError';
import type { AffiliateStatus } from '@/lib/tolt';

// Rewardful affiliate tracking, the program Dabble is moving off. Runs alongside
// Tolt through the transition (see `@/lib/tolt`). The script records the click
// when someone arrives on an affiliate link and resolves it into a referral id on
// `window.Rewardful.referral`, which we send with the signup so dabble-rest can
// tag the Stripe customer, the object Rewardful reads.
//
// Attribution applies to brand-new accounts only, so the id rides the signup
// request and nothing else.
const SCRIPT_ID = 'rewardful-js';
const SCRIPT_SRC = 'https://r.wdfl.co/rw.js';

/**
 * How long a signup waits on rw.js before giving up on it. Only reached when the
 * script is blocked or the network is dead. A loaded script fires `ready` and the
 * wait ends there.
 */
const READY_TIMEOUT_MS = 2000;

type RewardfulQueue = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    _rwq?: string;
    rewardful?: RewardfulQueue;
    Rewardful?: { referral?: string };
  }
}

/**
 * Settles once the referral id can be read. Resolved by default so a page that
 * never loads Rewardful (no id, SSR) doesn't make callers wait.
 */
let ready = Promise.resolve();

let status: AffiliateStatus = 'not-loaded';

/** Load outcome of the Rewardful script, for the signup attribution event. */
export function getRewardfulStatus(): AffiliateStatus {
  return status;
}

/** Load the Rewardful tracking script once. No-op without an id. */
export function loadRewardful(rewardfulId: string | undefined) {
  if (!rewardfulId) return;
  if (typeof document === 'undefined' || document.getElementById(SCRIPT_ID)) return;

  // Rewardful's snippet installs a queuing stub first, so calls made before
  // rw.js lands are replayed when it arrives.
  const stub: RewardfulQueue = (...args: unknown[]) => {
    stub.q!.push(args);
  };
  stub.q = [];
  window._rwq = 'rewardful';
  window.rewardful = window.rewardful || stub;

  const script = document.createElement('script');
  script.async = true;
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.setAttribute('data-rewardful', rewardfulId);

  // rw.js doesn't publish the referral when it loads. It trades the affiliate's
  // `?via=` handle for an id over the network first, and `ready` is its signal for
  // that. Reading before it fires yields undefined, and Rewardful tags the Stripe
  // customer at creation only, so a signup that reads too early is unattributable
  // for good. The queuing stub exists to make this registration survive the wait.
  ready = new Promise<void>((resolve) => {
    window.rewardful!('ready', () => {
      status = 'loaded';
      resolve();
    });
    // Ad blocker, 404, CSP, dead network: `ready` never fires, so cap the wait
    // rather than holding a signup on a script that isn't coming.
    setTimeout(() => {
      if (status === 'not-loaded') status = 'timed-out';
      resolve();
    }, READY_TIMEOUT_MS);
    script.onerror = () => {
      status = 'failed';
      // Affiliate scripts are among the most-blocked categories, and a silent
      // failure here looks exactly like traffic no affiliate sent.
      logError('Rewardful script failed to load; signups will go unattributed', SCRIPT_SRC);
      resolve();
    };
  });

  document.head.appendChild(script);
}

/**
 * The current affiliate referral id, or undefined. Waits for rw.js to resolve it
 * (bounded by `READY_TIMEOUT_MS`) rather than reading whatever happens to be there,
 * so a fast submit on a slow connection is still attributed.
 */
export async function getRewardfulReferral(): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined;
  await ready;
  const referral = window.Rewardful?.referral;
  return typeof referral === 'string' && referral.trim() ? referral.trim() : undefined;
}

/** Test seam: drop the pending wait and load state between cases. */
export function resetRewardfulForTest() {
  ready = Promise.resolve();
  status = 'not-loaded';
}
